export const LEVEL_WIDTH=11, LEVEL_HEIGHT=9;
export const LEVEL_TOOLS=['floor','wall','light','landmark','key','exit','start'];
const index=(x,y)=>x+y*LEVEL_WIDTH;
const point=i=>({x:i%LEVEL_WIDTH,y:Math.floor(i/LEVEL_WIDTH)});
const neighbors=i=>{const {x,y}=point(i);return [[x+1,y],[x,y-1],[x-1,y],[x,y+1]].filter(([a,b])=>a>=0&&a<LEVEL_WIDTH&&b>=0&&b<LEVEL_HEIGHT).map(([a,b])=>index(a,b));};
const copy=value=>JSON.parse(JSON.stringify(value));

export function createLevel(preset='fork') {
  const cells=Array.from({length:LEVEL_WIDTH*LEVEL_HEIGHT},(_,i)=>{const {x,y}=point(i);return x===0||x===LEVEL_WIDTH-1||y===0||y===LEVEL_HEIGHT-1?'wall':'floor';});
  if(preset==='fork') {
    for(let y=1;y<=6;y++)if(y!==4)cells[index(4,y)]='wall';
    for(let x=6;x<=9;x++)if(x!==8)cells[index(x,5)]='wall';
  }
  for(const [x,y,type] of [[1,7,'start'],[8,2,'key'],[9,1,'exit'],[3,4,'light'],[7,2,'light'],[5,4,'landmark']])cells[index(x,y)]=type;
  return cells;
}

export function analyzeLevel(cells) {
  const start=cells.indexOf('start'),key=cells.indexOf('key'),exit=cells.indexOf('exit');
  const missing=start<0?'start':key<0?'key':exit<0?'exit':null;
  if(missing)return {valid:false,reachable:false,shortest:null,route:[],reason:`missing-${missing}`,reachableCells:0};
  const queue=[{cell:start,key:false,parent:-1}],seen=new Set([`${start}:0`]);
  let found=-1;
  for(let i=0;i<queue.length;i++) {
    const node=queue[i];if(node.cell===exit&&node.key){found=i;break;}
    for(const cell of neighbors(node.cell)) {
      if(cells[cell]==='wall'||cell===exit&&!node.key)continue;
      const hasKey=node.key||cell===key,state=`${cell}:${hasKey?1:0}`;
      if(seen.has(state))continue;seen.add(state);queue.push({cell,key:hasKey,parent:i});
    }
  }
  const route=[];for(let i=found;i>=0;i=queue[i].parent)route.unshift(point(queue[i].cell));
  return {valid:true,reachable:found>=0,shortest:found>=0?route.length-1:null,route,reason:found>=0?'reachable':'unreachable',reachableCells:new Set(queue.map(p=>p.cell)).size};
}

function lineOfSight(cells,from,to) {
  let x=from.x,y=from.y;const dx=Math.abs(to.x-x),dy=Math.abs(to.y-y),sx=x<to.x?1:-1,sy=y<to.y?1:-1;let error=dx-dy;
  while(x!==to.x||y!==to.y) {
    const e2=2*error;if(e2>-dy){error-=dy;x+=sx;}if(e2<dx){error+=dx;y+=sy;}
    if(x===to.x&&y===to.y)return true;
    if(cells[index(x,y)]==='wall')return false;
  }
  return true;
}

export function levelVisibility(cells,player) {
  const visible=new Set(),lights=cells.map((type,i)=>type==='light'?point(i):null).filter(Boolean);
  cells.forEach((type,i)=> {
    const p=point(i),distance=Math.hypot(p.x-player.x,p.y-player.y);
    const lit=lights.some(light=>Math.hypot(light.x-p.x,light.y-p.y)<=2.1&&lineOfSight(cells,light,p));
    if((distance<=2.3||distance<=8&&lit||distance<=5&&type==='landmark')&&lineOfSight(cells,player,p))visible.add(i);
  });
  return visible;
}

export class LevelDesignModel {
  constructor(preset='fork') {this.records={a:null,b:null};this.loadPreset(preset);}
  loadPreset(preset='fork') {this.cells=createLevel(preset);this.mode='author';this._clearRun();return this.analysis;}
  _clearRun() {
    const start=this.cells.indexOf('start');this.player=point(start>=0?start:index(1,7));this.hasKey=false;this.won=false;this.moves=0;this.blocked=0;this.duration=0;
    this.path=[{...this.player,time:0}];this.heat=Array(this.cells.length).fill(0);this.visits=Array(this.cells.length).fill(0);this.visits[index(this.player.x,this.player.y)]=1;
    this.seen=new Set();this._reveal();
  }
  get analysis(){return analyzeLevel(this.cells);}
  get visibility(){return levelVisibility(this.cells,this.player);}
  _reveal(){for(const cell of this.visibility)this.seen.add(cell);}
  paint(x,y,tool) {
    if(this.mode!=='author')return {ok:false,reason:'playing'};
    if(!Number.isInteger(x)||!Number.isInteger(y)||x<=0||x>=LEVEL_WIDTH-1||y<=0||y>=LEVEL_HEIGHT-1)return {ok:false,reason:'boundary'};
    if(!LEVEL_TOOLS.includes(tool))return {ok:false,reason:'tool'};
    if(['key','exit','start'].includes(tool))this.cells=this.cells.map(type=>type===tool?'floor':type);
    this.cells[index(x,y)]=tool;this._clearRun();return {ok:true,reason:'painted'};
  }
  startRun() {
    if(!this.analysis.valid)return {ok:false,reason:this.analysis.reason};
    this._clearRun();this.mode='player';return {ok:true,reason:'started'};
  }
  edit(){this.mode='author';return {ok:true,reason:'editing'};}
  move(dx,dy) {
    if(this.mode!=='player')return {ok:false,reason:'author'};
    if(this.won)return {ok:false,reason:'won'};
    if(!Number.isInteger(dx)||!Number.isInteger(dy)||Math.abs(dx)+Math.abs(dy)!==1)return {ok:false,reason:'direction'};
    const x=this.player.x+dx,y=this.player.y+dy,i=index(x,y),type=this.cells[i];
    if(x<0||x>=LEVEL_WIDTH||y<0||y>=LEVEL_HEIGHT||type==='wall'){this.blocked++;return {ok:false,reason:'wall'};}
    if(type==='exit'&&!this.hasKey){this.blocked++;return {ok:false,reason:'locked'};}
    this.player={x,y};this.moves++;this.visits[i]++;this.path.push({x,y,time:this.duration});this._reveal();
    const picked=type==='key'&&!this.hasKey;if(picked)this.hasKey=true;
    if(type==='exit'&&this.hasKey)this.won=true;
    return {ok:true,reason:this.won?'won':picked?'key':'moved'};
  }
  advance(dt) {
    if(this.mode!=='player'||this.won||!Number.isFinite(dt)||dt<=0)return;
    const elapsed=Math.min(dt,1);this.duration+=elapsed;this.heat[index(this.player.x,this.player.y)]+=elapsed;
  }
  record(slot) {
    if(!['a','b'].includes(slot))return null;
    const record={cells:[...this.cells],path:copy(this.path),heat:[...this.heat],visits:[...this.visits],moves:this.moves,blocked:this.blocked,duration:this.duration,won:this.won,hasKey:this.hasKey,shortest:this.analysis.shortest};
    this.records[slot]=copy(record);return record;
  }
}
